import { type FormEvent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "../app/AppContext";
import { AuthErrorBox, PasswordField } from "./AuthGate";
import styles from "./AuthGate.module.css";
import { AuthFlowError } from "./auth-errors";

/**
 * The shared Firebase action handler (/auth/action), hosted on the canonical
 * MFA host via the Firebase console's custom action URL and rendered as the
 * V1-styled card on BOTH sites. Handles verifyEmail, resetPassword, and
 * recoverEmail codes, including invalid/expired/already-used ones. Any other
 * mode (including sign-in links from the removed passwordless flow) renders
 * the invalid-link card.
 *
 * The continueUrl query param is attacker-influenceable: it is only ever
 * followed after the API vouches for it (HTTPS + configured MFA/LYA host);
 * anything else falls back to /login.
 */

type ActionState =
  | { kind: "working" }
  | { kind: "invalid"; error: unknown }
  | { kind: "verified" }
  | { kind: "reset-form"; email: string }
  | { kind: "reset-done"; signedIn: boolean }
  | { kind: "recovered"; email: string | null };

/** Action codes are single-use; share one attempt across StrictMode's double mount. */
const actionAttempts = new Map<string, Promise<ActionState>>();

/**
 * Completing a reset signs the user in, and adopting that session flips the
 * public tree to the member tree — which REMOUNTS this page. The remounted
 * instance must land on the flow's terminal state, not re-derive "show the
 * reset form" from the cached attempt.
 */
const terminalStates = new Map<string, ActionState>();

export function AuthActionPage() {
  const context = useAppContext();
  const intl = useIntl();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get("mode");
  const oobCode = params.get("oobCode");
  const rawContinueUrl = params.get("continueUrl");

  const [state, setState] = useState<ActionState>({ kind: "working" });
  const [continueUrl, setContinueUrl] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<unknown>(null);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const configured = Boolean(context.runtimeConfig.firebase);

  // Never follow a continuation the server hasn't vouched for.
  useEffect(() => {
    if (!rawContinueUrl) return;
    let cancelled = false;
    void context.api
      .validateContinuation(rawContinueUrl)
      .then((result) => {
        if (!cancelled) setContinueUrl(result.continueUrl);
      })
      .catch(() => {
        if (!cancelled) setContinueUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [context.api, rawContinueUrl]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: one attempt per (mode, code) — deduped for StrictMode
  useEffect(() => {
    if (!configured) return;
    if (!mode || !oobCode) {
      // Malformed link: decide without any network call.
      setState({ kind: "invalid", error: new AuthFlowError("invalid_link") });
      return;
    }
    let cancelled = false;
    const key = `${mode}:${oobCode}`;
    const terminal = terminalStates.get(key);
    if (terminal) {
      setState(terminal);
      return;
    }
    let attempt = actionAttempts.get(key);
    if (!attempt) {
      attempt = (async (): Promise<ActionState> => {
        const firebase = await import("./firebase-auth");
        if (mode === "verifyEmail") {
          await firebase.applyVerifyEmailCode(context.runtimeConfig, oobCode);
          // A live Firebase session lets us flip the banner (and Postgres)
          // immediately; without one the user re-checks from the banner later.
          try {
            await context.refreshVerification();
          } catch {
            /* verification applied — the signed-out path just can't re-exchange */
          }
          return { kind: "verified" };
        }
        if (mode === "resetPassword") {
          const email = await firebase.verifyResetCode(context.runtimeConfig, oobCode);
          return { kind: "reset-form", email };
        }
        if (mode === "recoverEmail") {
          const email = await firebase.applyRecoverEmailCode(context.runtimeConfig, oobCode);
          return { kind: "recovered", email };
        }
        return { kind: "invalid", error: new AuthFlowError("invalid_link") };
      })();
      actionAttempts.set(key, attempt);
      attempt.catch(() => actionAttempts.delete(key));
    }
    attempt
      .then((result) => {
        if (!cancelled) setState(result);
      })
      .catch((error) => {
        if (!cancelled) setState({ kind: "invalid", error });
      });
    return () => {
      cancelled = true;
    };
  }, [configured, mode, oobCode]);

  function onContinue() {
    if (continueUrl) {
      window.location.assign(continueUrl);
      return;
    }
    navigate(context.viewer ? "/" : "/login");
  }

  function onSubmitReset(event: FormEvent) {
    event.preventDefault();
    if (state.kind !== "reset-form") return;
    if (newPassword !== confirmPassword) {
      setFormError(new AuthFlowError("password_mismatch"));
      return;
    }
    setBusy(true);
    setFormError(null);
    void (async () => {
      const firebase = await import("./firebase-auth");
      try {
        await firebase.completePasswordReset(context.runtimeConfig, oobCode ?? "", newPassword);
      } catch (error) {
        setFormError(error);
        setBusy(false);
        return;
      }
      // The password IS changed from here on. Best effort: sign straight in
      // with it and revoke every other session for this principal (both
      // sites) while keeping this fresh one — all BEFORE adopting the session,
      // because adoption re-renders the app into the member tree and remounts
      // this page onto the recorded terminal state.
      let signedIn = false;
      try {
        const idToken = await firebase.signInWithPassword(
          context.runtimeConfig,
          state.email,
          newPassword,
        );
        await context.api.exchangeAuth(idToken, context.locale);
        // The session cookie now belongs to the reset principal, so the
        // account-boundary adoption below MUST run — a best-effort revocation
        // failure may not flip signedIn back off and leave a previous
        // account's cache rendering under the new session.
        signedIn = true;
        try {
          await context.revokeOtherSessions();
        } catch {
          /* best effort — unrevoked sibling sessions expire on their own */
        }
      } catch {
        /* fall through to the "sign in with your new password" outcome */
      }
      const done: ActionState = { kind: "reset-done", signedIn };
      terminalStates.set(`${mode}:${oobCode}`, done);
      setState(done);
      setBusy(false);
      if (signedIn) await context.adoptSession();
    })();
  }

  function onSendReset(email: string) {
    setBusy(true);
    void context
      .sendPasswordReset(email)
      .then(() => setResetEmailSent(true))
      .catch((error) => setFormError(error))
      .finally(() => setBusy(false));
  }

  const errorCode =
    state.kind === "invalid" && state.error instanceof AuthFlowError ? state.error.code : null;

  return (
    <div className={styles.cardPage}>
      <section className={styles.card} aria-labelledby="auth-action-title">
        {!configured ? (
          <p className={`${styles.notice} ${styles.configError}`} role="alert">
            <FormattedMessage id="auth.configMissing" />
          </p>
        ) : state.kind === "working" ? (
          <p className={styles.notice} role="status">
            <FormattedMessage id="auth.action.working" />
          </p>
        ) : state.kind === "verified" ? (
          <>
            <h1 id="auth-action-title" className={styles.cardTitle}>
              <FormattedMessage id="auth.action.verifiedTitle" />
            </h1>
            <p className={styles.cardSub}>
              <FormattedMessage id="auth.action.verifiedBody" />
            </p>
            <button className={styles.primaryCta} type="button" onClick={onContinue}>
              <FormattedMessage id="auth.action.continue" />
            </button>
          </>
        ) : state.kind === "reset-form" ? (
          <>
            <h1 id="auth-action-title" className={styles.cardTitle}>
              <FormattedMessage id="auth.action.resetTitle" />
            </h1>
            <p className={styles.cardSub}>
              <FormattedMessage id="auth.action.resetFor" values={{ email: state.email }} />
            </p>
            <form onSubmit={onSubmitReset}>
              <div className={styles.fields}>
                <PasswordField
                  value={newPassword}
                  onChange={setNewPassword}
                  show={showPassword}
                  onToggleShow={() => setShowPassword(!showPassword)}
                  disabled={busy}
                  autoComplete="new-password"
                  labelId="auth.action.newPasswordLabel"
                />
                <label className={styles.cardLabel}>
                  <FormattedMessage id="auth.action.confirmPasswordLabel" />
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    placeholder={intl.formatMessage({ id: "auth.passwordPlaceholder" })}
                    disabled={busy}
                  />
                </label>
              </div>
              <AuthErrorBox error={formError} />
              <button className={styles.primaryCta} type="submit" disabled={busy}>
                {busy ? (
                  <FormattedMessage id="auth.busy" />
                ) : (
                  <FormattedMessage id="auth.action.resetCta" />
                )}
              </button>
            </form>
          </>
        ) : state.kind === "reset-done" ? (
          <>
            <h1 id="auth-action-title" className={styles.cardTitle}>
              <FormattedMessage id="auth.action.resetDoneTitle" />
            </h1>
            <p className={styles.cardSub}>
              <FormattedMessage
                id={state.signedIn ? "auth.action.resetDoneBody" : "auth.action.resetDoneSignIn"}
              />
            </p>
            <button className={styles.primaryCta} type="button" onClick={onContinue}>
              <FormattedMessage
                id={state.signedIn ? "auth.action.continue" : "auth.action.goSignIn"}
              />
            </button>
          </>
        ) : state.kind === "recovered" ? (
          <>
            <h1 id="auth-action-title" className={styles.cardTitle}>
              <FormattedMessage id="auth.action.recoverTitle" />
            </h1>
            <p className={styles.cardSub}>
              {state.email ? (
                <FormattedMessage id="auth.action.recoverBody" values={{ email: state.email }} />
              ) : (
                <FormattedMessage id="auth.action.recoverBodyNoEmail" />
              )}
            </p>
            {state.email ? (
              <button
                className={styles.linkCta}
                type="button"
                disabled={busy || resetEmailSent}
                onClick={() => state.email && onSendReset(state.email)}
              >
                {busy ? (
                  <FormattedMessage id="auth.busy" />
                ) : (
                  <FormattedMessage id="auth.action.recoverReset" />
                )}
              </button>
            ) : null}
            {resetEmailSent && state.email ? (
              <p className={styles.notice} role="status">
                <FormattedMessage id="auth.reset.sent" values={{ email: state.email }} />
              </p>
            ) : null}
            <AuthErrorBox error={formError} />
            <button className={styles.primaryCta} type="button" onClick={onContinue}>
              <FormattedMessage id="auth.action.continue" />
            </button>
          </>
        ) : (
          <>
            <h1 id="auth-action-title" className={styles.cardTitle}>
              <FormattedMessage
                id={
                  errorCode === "expired_link"
                    ? "auth.action.expiredTitle"
                    : "auth.action.invalidTitle"
                }
              />
            </h1>
            <p className={styles.cardSub}>
              <FormattedMessage
                id={
                  errorCode === "expired_link"
                    ? "auth.action.expiredBody"
                    : "auth.action.invalidBody"
                }
              />
            </p>
            <button className={styles.primaryCta} type="button" onClick={onContinue}>
              <FormattedMessage id="auth.action.goSignIn" />
            </button>
          </>
        )}
      </section>
    </div>
  );
}
